import { Button, Card, Dialog, Flex, FormStatus, P, Tooltip } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import type { ModalContentProps } from '@dnb/eufemia/components/modal/types';
import { Form, Iterate, Value } from '@dnb/eufemia/extensions/forms';
import { trash as TrashIcon } from '@dnb/eufemia/icons';
import type { JwkDto } from '@portals/shared/portal/JwksDto';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react';

import { deleteClientKey } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';

import { CrqField } from '../../../CrqField';

type FormSchemaJson = {
  crq: string | undefined;
};

type Props = {
  appId: string;
  clientId: string;
  liveMode: boolean;
  isAdmin: boolean;
  canManageKeys: boolean;
  distinctKidCount: number;
  noPrivilegeError: ReactNode;
  onKeyRemoved: () => Promise<void>;
};

const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required to remove a signing key from a client in the live environment. Enter the ID for the relevant CRQ.',
};

const lastKeyTooltip = (
  <Tooltip size="large" style={{ maxWidth: '300px' }}>
    A client must always keep at least one signing key. Add a new key before removing this one.
  </Tooltip>
);

export default function KeyRow({
  appId,
  clientId,
  liveMode,
  isAdmin,
  canManageKeys,
  distinctKidCount,
  noPrivilegeError,
  onKeyRemoved,
}: Props): JSX.Element {
  const { value } = Iterate.useItem<JwkDto>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const kid = value?.kid ?? '';
  const isLastKey = distinctKidCount <= 1;
  const removeDisabled = !canManageKeys || isLastKey;

  const removeTooltip = useMemo(() => {
    if (!canManageKeys) return noPrivilegeError;
    if (isLastKey) return lastKeyTooltip;
    return undefined;
  }, [canManageKeys, isLastKey, noPrivilegeError]);

  const onSubmit = useCallback(
    async ({ crq }: FormSchemaJson, close: () => void) => {
      try {
        setSubmitting(true);
        await deleteClientKey(appId, clientId, kid, crq);
        setErrorMessage(null);
        close();
        await onKeyRemoved();
      } catch (error: unknown) {
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else {
          throw error;
        }
      } finally {
        setSubmitting(false);
      }
    },
    [appId, clientId, kid, onKeyRemoved],
  );

  return (
    <Card innerSpace="small" stack>
      <Value.SummaryList layout="horizontal">
        <Value.String itemPath="/kid" label="Key ID" />
        <Value.String itemPath="/kty" label="Type" />
        <Value.String itemPath="/crv" label="Curve" />
      </Value.SummaryList>

      <Flex.Horizontal>
        <Dialog
          disabled={removeDisabled}
          title="Remove signing key"
          triggerAttributes={{
            text: 'Remove',
            icon: TrashIcon,
            iconPosition: 'left',
            variant: 'tertiary',
            tooltip: removeTooltip,
          }}
        >
          {({ close }: ModalContentProps) => (
            <Form.Handler<FormSchemaJson>
              data={{ crq: undefined }}
              onSubmit={(data) => onSubmit(data, () => close?.())}
            >
              {submitting && <LoadingModal />}
              <P>
                Key ID <strong>{kid}</strong>
              </P>
              <FormStatus bottom="medium" state="warning" top="medium">
                Removal can take a few minutes to take effect.
              </FormStatus>
              {isAdmin && liveMode && <CrqField customHelp={crqHelp} />}
              {errorMessage && <FormStatus bottom="medium" text={errorMessage} top="medium" />}
              <Button icon={TrashIcon} iconPosition="left" size="large" text="Remove key" top="small" type="submit" />
            </Form.Handler>
          )}
        </Dialog>
      </Flex.Horizontal>
    </Card>
  );
}
